/**
 * PURPOSE: Cliente HTTP de las inscripciones por categoría de un deporte individual (FTR-0059) y su estado.
 * RESPONSIBILITIES: Una función por endpoint de
 *   `/api/hayllis/v2/admin/individual-results/events/:idevent/sports/:idsport/...`, desenvolviendo `{ ok, data }`:
 *   estado del módulo, categorías con sus inscritos, buscar deportistas, agregar, retirar y mover entre categorías.
 *   `getIndividualEntriesStatus` nunca lanza (fail closed: todo apagado). El resto relanza el error de axios para que
 *   la interfaz lea `code` y `message` (individualEntriesErrorMessage).
 * COLLABORATORS: httpService.js, area/CategoriasIndividualesArea/CategoriasIndividualesArea.jsx.
 * ROUTE: winscore/src/services/individualEntriesService.js
 */
import httpService from "./httpService.js";

export const INDIVIDUAL_RESULTS_ADMIN_BASE = "/api/hayllis/v2/admin/individual-results";

export const INDIVIDUAL_ENTRIES_STATUS_OFF = Object.freeze({ enabled: false, reason: null, canManageEntries: false });

const scopePath = ({ idevent, idsport }) =>
  `${INDIVIDUAL_RESULTS_ADMIN_BASE}/events/${Number(idevent)}/sports/${Number(idsport)}`;

const unwrap = (response) => {
  const body = response?.data;
  if (body && typeof body === "object" && body.ok === false) {
    const error = new Error(body.message || "Respuesta no válida del servidor.");
    error.response = { status: response?.status ?? 200, data: body };
    throw error;
  }
  return body && typeof body === "object" && "data" in body ? body.data : body;
};

const request = async ({ method, scope, path = "", params, data }) => {
  const config = { method, url: `${scopePath(scope)}${path}` };
  if (params) config.params = params;
  if (data !== undefined) config.data = data;
  return unwrap(await httpService.request(config));
};

const categoryPath = (idcategory) => `/categories/${Number(idcategory)}/entries`;

export const getIndividualEntriesStatus = async ({ idevent, idsport }) => {
  try {
    const data = (await request({ method: "get", scope: { idevent, idsport }, path: "/status" })) || {};
    return {
      enabled: data.enabled === true,
      reason: typeof data.reason === "string" ? data.reason : null,
      canManageEntries: data.canManageEntries === true,
    };
  } catch (error) {
    const code = error?.response?.data?.code;
    return { ...INDIVIDUAL_ENTRIES_STATUS_OFF, reason: typeof code === "string" ? code : null };
  }
};

/** Categorías del deporte con sus inscritos (vigentes y retirados). */
export const listIndividualCategories = async ({ idevent, idsport }) => {
  const data = await request({ method: "get", scope: { idevent, idsport }, path: "/podiums" });
  return Array.isArray(data?.categories) ? data.categories : [];
};

export const searchIndividualCandidates = async ({ idevent, idsport, idcategory, query }) => {
  const data = await request({
    method: "get",
    scope: { idevent, idsport },
    path: `${categoryPath(idcategory)}/candidates`,
    params: { q: query },
  });
  return Array.isArray(data?.candidates) ? data.candidates : [];
};

export const addIndividualEntry = ({ idevent, idsport, idcategory, idacreditation }) =>
  request({ method: "post", scope: { idevent, idsport }, path: categoryPath(idcategory), data: { idacreditation: Number(idacreditation) } });

export const removeIndividualEntry = ({ idevent, idsport, idcategory, idacreditation }) =>
  request({ method: "delete", scope: { idevent, idsport }, path: `${categoryPath(idcategory)}/${Number(idacreditation)}` });

export const moveIndividualEntry = ({ idevent, idsport, idcategory, idacreditation, targetCategoryId }) =>
  request({
    method: "post",
    scope: { idevent, idsport },
    path: `${categoryPath(idcategory)}/${Number(idacreditation)}/move`,
    data: { targetCategoryId: Number(targetCategoryId) },
  });

/** Mensaje para el operador: el del servidor si viene; si no, uno genérico. */
export const individualEntriesErrorMessage = (error, fallback = "No se pudo completar la operación.") => {
  const message = error?.response?.data?.message;
  if (typeof message === "string" && message.trim()) return message;
  if (!error?.response) return "No hay conexión con el servidor. Revisa tu red e inténtalo de nuevo.";
  return fallback;
};

export const INELIGIBLE_REASON_LABELS = Object.freeze({
  ENTRY_NOT_ACTIVE: "Inscripción retirada",
  NOT_APPROVED: "Acreditación no aprobada",
  NOT_ATHLETE: "No está acreditado como deportista",
  INSTITUTION_NOT_PARTICIPATING: "Su universidad no participa en el deporte",
});
